import { CRMListItem } from "../../../../types/apiTypes";
import React, { ReactNode, useEffect, useState } from "react";
import { useScrollWrapper, useDebounceValue } from "../../../../hooks";
import {
  FormControl,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SearchIcon } from "../../../images/SearchIcon";
import { Dropdown } from "../../dropdown/Dropdown";
import { DataSourceAccountIcon, DataSourceContactIcon } from "../../../images";

const dataSourcesIcons: Record<string, ReactNode> = {
  accounts: <DataSourceAccountIcon />,
  contacts: <DataSourceContactIcon />,
};

export const DataSources = ({
  setSelectedData,
  crmList,
}: {
  setSelectedData: (data: string) => void;
  crmList: CRMListItem[];
}) => {
  const [items, setItems] = useState<CRMListItem[]>(crmList);
  const [dataSource, setDataSource] = useState<string>(crmList[0].id);
  const [option, setOption] = useState<string>("All");
  const [searchValue, setSearchValue] = useState<string>("");
  const { scrollRef } = useScrollWrapper({ trigger: items });

  const debounceValue = useDebounceValue(searchValue);

  useEffect(() => {
    setSelectedData(dataSource);
  }, [dataSource]);

  useEffect(() => {
    setItems(crmList);
    setDataSource(crmList[0].id);
  }, [crmList]);

  useEffect(() => {
    setItems(
      crmList.filter((crm) => {
        const isSearch =
          !debounceValue ||
          crm.name.toLowerCase().includes(debounceValue.toLowerCase());

        const isOption =
          option === "All" ? true : crm.object_type === option.toLowerCase();
        return isSearch && isOption;
      }),
    );
  }, [debounceValue, option]);

  return (
    <Stack gap="8px">
      <Typography
        component="span"
        variant="subtitle1"
        color="custom.textDarkBlue"
        fontWeight={500}
      >
        Choose data sources
      </Typography>
      <Stack flexDirection="row" gap="4px" alignItems="center">
        <TextField
          value={searchValue}
          variant="outlined"
          placeholder="Search"
          onChange={(e) => setSearchValue(e.target.value)}
          sx={{
            width: "100%",
            maxWidth: "64%",
            color: "#1F2932",
            borderRadius: "8px",
            textAlign: "center",
            "& .MuiOutlinedInput-root": {
              padding: 0,
            },
            "& input": {
              padding: "0",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment
                position="start"
                sx={{
                  paddingLeft: "12px",
                  margin: 0,
                }}
              >
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Dropdown
          label=""
          selectedItem={{ value: option }}
          onChange={({ value }) => setOption(value)}
          items={[
            { value: "All" },
            { value: "Accounts" },
            { value: "Contacts" },
          ]}
          placeholder=""
          maxHeight="134px"
        />
      </Stack>
      <Stack
        ref={scrollRef}
        sx={{
          maxHeight: "200px",
          minHeight: "100px",
          overflowY: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#aaa",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-track": {
            width: "12px",
            backgroundColor: "transparent",
          },
        }}
      >
        {items.length === 0 ? (
          <Typography
            variant="subtitle1"
            color="#1F293299"
            align="center"
            paddingTop="16px"
          >
            No data sources
          </Typography>
        ) : (
          <>
            <FormControl>
              <RadioGroup
                aria-labelledby="demo-radio-buttons-group-label"
                defaultValue={dataSource}
                value={dataSource}
                name="radio-buttons-group"
                onChange={(e, el) => setDataSource(el)}
              >
                {items.map(({ id, object_type, name }) => {
                  return (
                    <Stack
                      key={id}
                      onClick={() => setDataSource(id)}
                      sx={{
                        flexDirection: "row",
                        flexGrow: 1,
                        alignItems: "center",
                        padding: "4px 16px",
                        cursor: "pointer",
                        "&:hover": {
                          backgroundColor: "rgba(28, 60, 108, 0.08)",
                        },
                      }}
                    >
                      <FormControlLabel
                        key={id}
                        value={id}
                        control={<Radio />}
                        sx={{ marginRight: 0 }}
                        label={""}
                      />
                      <Typography
                        variant="subtitle1"
                        color="#000000DE"
                        component="span"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {name}
                      </Typography>
                      <Stack marginLeft="auto" minWidth="20px" minHeight="20px">
                        {dataSourcesIcons[object_type]}
                      </Stack>
                    </Stack>
                  );
                })}
              </RadioGroup>
            </FormControl>
          </>
        )}
      </Stack>
    </Stack>
  );
};
